import { useCallback, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../api/client";
import type { SuccessMetric } from "../hooks/usePlay";

interface MetricDelta {
  metricLabel: string;
  baseline: string | null;
  start: string | null;
  end: string | null;
  target: string | null;
  deltaNumeric: number | null;
  verdict: "improved" | "regressed" | "unchanged" | "unknown";
}

interface TrendPoint {
  playId: string;
  capturedAt: string;
  value: number;
}

interface MetricTrend {
  metricLabel: string;
  points: TrendPoint[];
  averageEnd: number | null;
  completeInstanceCount: number;
}

interface GovernanceResponse {
  deltas: MetricDelta[];
  trend: MetricTrend[];
  snapshots: Array<{
    id: string;
    metricLabel: string;
    kind: "start" | "end";
    value: string;
    capturedAt: string;
  }>;
}

interface Props {
  workspaceId: string;
  playId: string;
  successMetrics: SuccessMetric[];
}

/**
 * Mini-sparkline rendered inline via SVG. ~80×24px. Renders a polyline
 * across N normalized points + a circle on the most recent point.
 * Lightweight enough to keep without a chart dependency.
 */
function Sparkline({ points }: { points: TrendPoint[] }) {
  if (points.length < 2) return null;
  const w = 80;
  const h = 24;
  const xs = points.map((_, i) => (i / (points.length - 1)) * w);
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const ys = vals.map((v) => h - ((v - min) / range) * h);
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${ys[i]!.toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <path d={path} fill="none" stroke="var(--accent, #6366f1)" strokeWidth={1.5} />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r={2.5} fill="var(--accent, #6366f1)" />
    </svg>
  );
}

function verdictPill(v: MetricDelta["verdict"]): { label: string; color: string; bg: string } {
  switch (v) {
    case "improved":  return { label: "improved",  color: "#15803d", bg: "#dcfce7" };
    case "regressed": return { label: "regressed", color: "#b91c1c", bg: "#fee2e2" };
    case "unchanged": return { label: "unchanged", color: "#854d0e", bg: "#fef9c3" };
    default:          return { label: "no data",   color: "var(--text-3)", bg: "var(--bg-soft)" };
  }
}

function MetricCaptureRow({
  playId,
  workspaceId,
  metricLabel,
  kind,
  currentValue,
  onSaved,
}: {
  playId: string;
  workspaceId: string;
  metricLabel: string;
  kind: "start" | "end";
  currentValue: string | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentValue ?? "");
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (v: string) =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/metric-snapshots`,
        { metric_label: metricLabel, kind, value: v },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["governance", playId] });
      setEditing(false);
      onSaved();
    },
  });

  if (!editing) {
    return (
      <span
        onClick={() => { setValue(currentValue ?? ""); setEditing(true); }}
        style={{
          cursor: "pointer",
          color: currentValue ? "var(--text-1)" : "var(--text-3)",
          textDecoration: "underline dotted",
          textUnderlineOffset: 2,
        }}
      >
        {currentValue ?? "+ capture"}
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
        placeholder={kind === "start" ? "start value" : "end value"}
        style={{ width: 90, padding: "2px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 4 }}
      />
      <button
        type="button"
        onClick={() => mut.mutate(value)}
        disabled={mut.isPending || !value.trim()}
        style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer" }}
      >
        {mut.isPending ? "…" : "save"}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer" }}
      >
        ×
      </button>
    </span>
  );
}

export function GovernancePanel({ workspaceId, playId, successMetrics }: Props) {
  const q = useQuery<GovernanceResponse>({
    queryKey: ["governance", playId],
    queryFn: () =>
      apiGet<GovernanceResponse>(
        `/api/workspaces/${workspaceId}/plays/${playId}/governance`,
      ),
  });

  const handleSaved = useCallback(() => { /* invalidate happens in the row */ }, []);

  if (successMetrics.length === 0) {
    return (
      <div data-testid="play-governance" style={{ padding: "24px 20px", color: "var(--text-3)", fontSize: 13, textAlign: "center" }}>
        No success metrics declared on this play. Add metrics on the Outcome tab to enable before/after tracking.
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div data-testid="play-governance" style={{ padding: 20, color: "var(--text-3)", fontSize: 13 }}>
        Loading governance…
      </div>
    );
  }

  const deltas = q.data?.deltas ?? [];
  const trend = q.data?.trend ?? [];

  return (
    <div data-testid="play-governance" style={{ padding: "16px 0" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
        Before / after
      </div>
      <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--bg-soft)", textAlign: "left", color: "var(--text-3)" }}>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Metric</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Baseline</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Start</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>End</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Target</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Δ</th>
              <th style={{ padding: "8px 10px", fontWeight: 600 }}>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {deltas.map((d) => {
              const pill = verdictPill(d.verdict);
              const deltaStr =
                d.deltaNumeric === null
                  ? "—"
                  : d.deltaNumeric > 0
                    ? `+${d.deltaNumeric.toFixed(2)}`
                    : d.deltaNumeric.toFixed(2);
              return (
                <tr key={d.metricLabel} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "8px 10px", color: "var(--text-1)", fontWeight: 600 }}>{d.metricLabel}</td>
                  <td style={{ padding: "8px 10px", color: "var(--text-2)" }}>{d.baseline ?? "—"}</td>
                  <td style={{ padding: "8px 10px", color: "var(--text-2)" }}>
                    <MetricCaptureRow
                      workspaceId={workspaceId}
                      playId={playId}
                      metricLabel={d.metricLabel}
                      kind="start"
                      currentValue={d.start}
                      onSaved={handleSaved}
                    />
                  </td>
                  <td style={{ padding: "8px 10px", color: "var(--text-2)" }}>
                    <MetricCaptureRow
                      workspaceId={workspaceId}
                      playId={playId}
                      metricLabel={d.metricLabel}
                      kind="end"
                      currentValue={d.end}
                      onSaved={handleSaved}
                    />
                  </td>
                  <td style={{ padding: "8px 10px", color: "var(--text-2)" }}>{d.target ?? "—"}</td>
                  <td style={{ padding: "8px 10px", color: "var(--text-1)", fontVariantNumeric: "tabular-nums" }}>{deltaStr}</td>
                  <td style={{ padding: "8px 10px" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: pill.color, background: pill.bg, padding: "2px 6px", borderRadius: 4 }}>
                      {pill.label.toUpperCase()}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {trend.length > 0 ? (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 20, marginBottom: 8 }}>
            Trend across recurring instances
          </div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
            {trend.map((t) => (
              <div key={t.metricLabel} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "var(--text-1)" }}>{t.metricLabel}</div>
                <Sparkline points={t.points} />
                <div style={{ fontSize: 11, color: "var(--text-3)", minWidth: 110, textAlign: "right" }}>
                  avg {t.averageEnd?.toFixed(2) ?? "—"} · {t.points.length} runs
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
